import { supabase } from '../supabaseClient';

// Client side of the admin-rpc gateway.
//
// The browser used to hold REACT_APP_ADMIN_API_KEY -- a shared secret that
// shipped in the bundle, so anyone who viewed source could call every admin_*
// RPC directly. It holds nothing now. Authorisation is the caller's ordinary
// Supabase session JWT, checked server-side against the admin_users allowlist,
// and the admin key is injected by the edge function where the browser can
// never see it.
//
// Every admin call in the app goes through this module. Nothing else should
// invoke an admin edge function or call an admin_* RPC directly -- after the
// part 2 revokes, a direct supabase.rpc('admin_...') fails with a permission
// error rather than doing anything useful.

// Returned in `error.status` so callers can tell the two failure modes apart:
// 401 means "no valid session" (show the login form), 403 means "signed in but
// not an admin" (say so, then sign out). Collapsing them would make a
// non-admin's session look like a login prompt they can never satisfy.
export const ADMIN_UNAUTHENTICATED = 401;
export const ADMIN_FORBIDDEN = 403;

function adminError(message, status = null, code = null) {
  return { message, status, code };
}

// supabase-js attaches the session JWT to functions.invoke() on its own, but it
// does so from auth state it updates asynchronously -- immediately after
// signInWithPassword resolves, an invoke can still go out carrying the anon key.
// The gateway rejects that with a 401 that looks exactly like an expired
// session. Reading the token here and passing it explicitly removes the race and
// lets a missing session fail locally instead of costing a round trip.
async function bearerToken() {
  const { data, error } = await supabase.auth.getSession();
  if (error) return { token: null, error: adminError(error.message, ADMIN_UNAUTHENTICATED) };
  const token = data?.session?.access_token || null;
  if (!token) {
    return { token: null, error: adminError('You are not signed in.', ADMIN_UNAUTHENTICATED) };
  }
  return { token, error: null };
}

// Pull the real message out of a non-2xx edge function response.
//
// functions.invoke() reports every non-2xx as the same opaque "Edge Function
// returned a non-2xx status code" and hides the body on error.context (a
// Response). Without this unwrap the dashboard would surface that string for a
// missing argument, a Postgres constraint violation and a non-admin account
// alike -- and the 401/403 split this module exists to expose would be lost.
async function unwrapInvokeError(error) {
  const res = error?.context;
  if (!res || typeof res.status !== 'number') {
    return adminError(error?.message || 'Admin request failed.');
  }
  let body = null;
  try {
    body = await res.clone().json();
  } catch {
    // Non-JSON body (a platform-level 5xx, a gateway timeout). Status is still
    // meaningful even when the body is not.
  }
  return adminError(
    body?.error || error.message || `Admin request failed (${res.status}).`,
    res.status,
    body?.code ?? null
  );
}

// Invoke an admin edge function with the caller's session.
//
// `body` may be a plain object (sent as JSON) or FormData -- upload-resource-pdf
// takes multipart. Content-Type is deliberately never set here: supabase-js
// picks it from the body type, and forcing it would break the multipart
// boundary.
export async function adminInvoke(name, body) {
  const { token, error: tokenErr } = await bearerToken();
  if (tokenErr) return { data: null, error: tokenErr };

  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (error) return { data: null, error: await unwrapInvokeError(error) };

  // A 200 carrying {error: ...}. The storage functions answer this way for
  // partial failures, so it is not necessarily a hard error -- hand the body
  // back and let the caller decide.
  return { data, error: null };
}

// Call an admin_* RPC through the gateway.
//
// Returns the same { data, error } shape as supabase.rpc() so call sites keep
// their existing destructuring; `error` carries .message like a PostgrestError
// plus .status and .code. Never pass admin_key -- the gateway rejects any args
// object containing one, on purpose, so an un-migrated call site fails loudly.
export async function adminRpc(fn, args = {}) {
  const { data, error } = await adminInvoke('admin-rpc', { fn, args });
  if (error) return { data: null, error };

  // The gateway's own error channel: a 400 from the underlying RPC comes back
  // through unwrapInvokeError above, but a body-level error on a 200 would
  // otherwise read as success with data === undefined.
  if (data?.error) return { data: null, error: adminError(data.error, null, data.code ?? null) };

  return { data: data?.data ?? null, error: null };
}

// The dashboard's admin gate.
//
// The browser cannot read admin_users itself -- RLS on, zero policies, no grants
// to anon or authenticated -- so asking the gateway is the only way to learn
// whether this session is an admin. Handled by the gateway before the allowlist
// because it is not an RPC.
export async function adminWhoami() {
  return adminRpc('whoami');
}
